import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Button,
  Slider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import styled from "styled-components";

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-input {
    padding: 0;
    height: 0;
    visibility: hidden;
  }
`;

function BulkEditDialog({
  open,
  onClose,
  selectedRows,
  onSave,
  openSnackbar,
  setRowSelection,
}) {
  const [formData, setFormData] = useState({
    qty: "",
    priority: 1,
    start_date: null,
    due_date: null,
  });
  const [enabledFields, setEnabledFields] = useState({
    qty: false,
    priority: false,
    start_date: false,
    due_date: false,
  });
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  const handleFieldToggle = (field) => {
    setEnabledFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };
  const handleSave = async () => {
    const updatedData = {};
    Object.keys(enabledFields).forEach((field) => {
      if (enabledFields[field]) {
        if (field === "start_date" || field === "due_date") {
          updatedData[field] = formData[field]
            ? formData[field].format("YYYY-MM-DD HH:mm:ss")
            : null;
        } else {
          updatedData[field] = formData[field];
        }
      }
    });
    if (Object.keys(updatedData).length === 0) {
      openSnackbar("수정할 항목을 선택해주세요.", "warning");
      return;
    }
    try {
      const updatePromises = selectedRows.map((row) => {
        const updateObj = {
          order_id: row.original.order_id,
          order_name: row.original.order_name,
          color: row.original.color,
          item_id: row.original.item_id,
          qty: enabledFields.qty ? Number(updatedData.qty) : row.original.qty,
          start_date: enabledFields.start_date
            ? updatedData.start_date
            : row.original.start_date,
          due_date: enabledFields.due_date
            ? updatedData.due_date
            : row.original.due_date,
          priority: enabledFields.priority
            ? updatedData.priority
            : row.original.order_priority,
        };
        return onSave({ values: updateObj });
      });
      await Promise.all(updatePromises);
      openSnackbar(`${selectedRows.length}개 항목이 수정되었습니다.`);
      if (setRowSelection) {
        setRowSelection({});
      }
      handleClose();
    } catch (error) {
      openSnackbar("수정 중 오류가 발생했습니다.", "error");
    }
  };
  const handleClose = () => {
    setFormData({
      qty: "",
      priority: 1,
      start_date: null,
      due_date: null,
    });
    setEnabledFields({
      qty: false,
      priority: false,
      start_date: false,
      due_date: false,
    });
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      PaperProps={{
        sx: {
          maxWidth: "444px",
        },
      }}
    >
      <DialogTitle
        variant="h5"
        sx={{ fontSize: "18px !important", color: "#111" }}
      >
        선택 항목 일괄 수정 ({selectedRows.length}개)
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "1.5rem", pt: 2 }}
      >
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledFields.qty}
                onChange={() => handleFieldToggle("qty")}
              />
            }
            label="수량 수정"
            sx={{ mb: 1 }}
          />
          <TextField
            label="수량"
            type="number"
            value={formData.qty}
            onChange={(e) => handleInputChange("qty", e.target.value)}
            disabled={!enabledFields.qty}
            fullWidth
          />
        </Box>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledFields.priority}
                onChange={() => handleFieldToggle("priority")}
              />
            }
            label="우선순위 수정"
            sx={{ mb: 1 }}
          />
          <StyledTextField
            label="우선순위"
            fullWidth
            disabled={!enabledFields.priority}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <Box sx={{ width: "2000%", px: 1, py: 2 }}>
                  <Slider
                    value={formData.priority}
                    onChange={(_, value) =>
                      handleInputChange("priority", value)
                    }
                    min={1}
                    max={10}
                    step={1}
                    valueLabelDisplay="off"
                    disabled={!enabledFields.priority}
                    sx={{
                      "& .MuiSlider-rail": {
                        opacity: 0.3,
                      },
                      "& .MuiSlider-thumb": {
                        width: 16,
                        height: 16,
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 1,
                      mt: 1,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      "& .selected": {
                        fontWeight: 700,
                        color: "text.primary",
                      },
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                      <span
                        key={num}
                        className={num === formData.priority ? "selected" : ""}
                      >
                        {num}
                      </span>
                    ))}
                  </Box>
                </Box>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledFields.start_date}
                onChange={() => handleFieldToggle("start_date")}
              />
            }
            label="생산 시작 수정"
            sx={{ mb: 1 }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              label="생산 시작"
              value={formData.start_date}
              onChange={(date) => handleInputChange("start_date", date)}
              format="YYYY년 MM월 DD일 HH시 mm분"
              disabled={!enabledFields.start_date}
              sx={{ width: "100%" }}
            />
          </LocalizationProvider>
        </Box>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={enabledFields.due_date}
                onChange={() => handleFieldToggle("due_date")}
              />
            }
            label="납기일 수정"
            sx={{ mb: 1 }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              label="납기일"
              value={formData.due_date}
              onChange={(date) => handleInputChange("due_date", date)}
              format="YYYY년 MM월 DD일 HH시 mm분"
              disabled={!enabledFields.due_date}
              sx={{ width: "100%" }}
            />
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          취소
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BulkEditDialog;
